import React, { useContext } from 'react';
import { Form, Input, Button } from "antd"
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { AppContext } from "../context/AppContext";

export const CreatePassword = () => {
    const placeHolderLink = "https://www.sukiadvantage.com/help-center"
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const context = useContext(AppContext);
    const { onChangePassword } = context;
    const [loading, setLoading] = React.useState(false)
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

    const backgroundImage = require('../assets/images/background.png');
    const sukiLogo = require('../assets/images/suki-logo-white.png');

    const backgroundStyle = {
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        height: '100vh',
        width: '100%',
        overflowY: 'hidden' as 'hidden',
    };

    const loginPanelStyle = {
        boxShadow: '-10px 12px 28px rgba(53, 54, 119, 0.37)',
        width: '450px',
    };

    const onFinish = async (values: any) => {
        const { password } = values;
        const payload = {
            new_password: password
        };

        try {
            setLoading(true)
            const response = await onChangePassword(payload)

            if (response) {
                const userRole = localStorage.getItem('accountType');
                navigate(userRole === 'partner' ? '/partners/transactions' : '/operations/transactions');
            }
        } catch (e) {
            console.error('[Change Password Error] ' || e.response?.status)
            setErrorMessage('There was a problem updating your password.');
        } finally {
            setLoading(false)
        }

    };

    const onValuesChange = () => {
        setErrorMessage(null);
    };

    const validatePassword = (rule: any, value: any) => {
        if (!value || value.length < 8) {
            return Promise.reject(new Error('Password must be at least 8 characters'));
        }
        if (!/[A-Z]/.test(value)) {
            return Promise.reject(new Error('Password must contain at least one uppercase character'));
        }
        if (!/[a-z]/.test(value)) {
            return Promise.reject(new Error('Password must contain at least one lowercase character'));
        }
        if (!/[0-9]/.test(value)) {
            return Promise.reject(new Error('Password must contain at least one number'));
        }
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
            return Promise.reject(new Error('Password must contain at least one special character'));
        }
        return Promise.resolve();
    };

    return (
        <div style={backgroundStyle}>
            <div className="flex flex-col justify-center items-center h-screen">
                <img src={sukiLogo} className="h-48" alt="Suki Logo" />
                <div className="bg-neutralWhite-25 rounded-2xl login-panel p-11 mt-8" style={loginPanelStyle}>
                    <Form form={form} onFinish={onFinish} onValuesChange={onValuesChange}>
                        <h1 className="text-3xl text-neutralBlack mb-2 font-poppinsSemiBold">
                            Welcome aboard!
                        </h1>
                        <p className="text-sm font-normal text-neutralBlack-75 font-poppinsRegular mb-4">
                            Please create your password to proceed
                        </p>
                        <p className="text-sm font-normal text-neutralBlack-75 font-poppinsRegular mb-0">
                            In order to protect your account, make sure your password:
                        </p>
                        <ul className="text-sm font-normal text-neutralBlack-75 font-poppinsRegular ml-8 mb-4 list-disc">
                            <li>Must be at least 8 characters</li>
                            <li>Must contain at least one uppercase character</li>
                            <li>Must contain at least one lowercase character</li>
                            <li>Must contain at least one number</li>
                            <li>Must contain at least one special character</li>
                        </ul>

                        <label className="text-sm text-neutralGray font-medium font-poppinsRegular required">
                            Input new password
                        </label>
                        <Form.Item name="password" className="w-full"
                            rules={[{ validator: validatePassword }]}>
                            <Input.Password className="h-12"
                                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                            />
                        </Form.Item>

                        <label className="text-sm text-neutralGray font-medium font-poppinsRegular required">
                            Confirm password
                        </label>
                        <Form.Item name="confirmPassword" className="w-full"
                            rules={[{ required: true, message: 'Please confirm your password' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('The passwords do not match'));
                                },
                            })]}>
                            <Input.Password className="h-12"
                                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                            />
                        </Form.Item>

                        <Form.Item>
                            <Form.ErrorList className="text-red-500 mb-2 -mt-2 text-body3" errors={[errorMessage]} />

                            <Button
                                htmlType="submit"
                                className="w-full text-neutralWhite bg-primary text-base font-poppinsMedium leading-6 border-primary hover:bg-primary-hovered hover:text-neutral-50 active:border-primary-border active:bg-primary-clicked py-2 mt-1 h-12"
                                loading={loading}
                                disabled={loading}
                            >
                                {loading ? 'Processing' : 'Proceed'}
                            </Button>
                        </Form.Item>

                        <p className='text-base font-poppinsRegular text-center whitespace-nowrap'>
                            Don't have an account? <a href={placeHolderLink} className="underline text-primary text-base font-semibold font-poppinsSemiBold">Contact Administrator</a>
                        </p>

                    </Form>
                </div>
            </div>
        </div>
    );
};
